import React, { useEffect, useMemo, useRef, useState } from "react";

import { SubscriptionIntervals, SubscriptionPlans } from "@prisma/client";
import clsx from "clsx";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";

import Button from "@musicfy/components/Button";
import Card from "@musicfy/components/Card";
import Icon from "@musicfy/components/Icon";
import SaleOfferHeading from "@musicfy/components/SaleOfferHeading";
import Switcher from "@musicfy/components/Switcher";
import { SubscriptionPlanInfo } from "@musicfy/contants/Subscriptions";
import { usePaddleContext } from "@musicfy/libs/PaddleProvider";
import { usePaddleCheckout } from "@musicfy/libs/PaddleProvider/hooks";
import { useCheckoutProvider } from "@musicfy/libs/PaddleProvider/hooks/useCheckoutProvider";
import { useStripeCheckout } from "@musicfy/libs/PaddleProvider/hooks/useStripeCheckout";
import { useToastContext } from "@musicfy/libs/ToastProvider";
import {
  getPricingCloseDestination,
  getCheckoutPromoCode,
} from "@musicfy/utils/checkoutPromo";
import {
  getPendingCheckout,
  takePendingCheckout,
} from "@musicfy/utils/pendingCheckout";

import FAQ from "./FAQ";
import FeaturesBreakdown from "./FeaturesBreakdown";
import MemorialDayPlanCard from "./MemorialDayPlanCard";
import FullScreenWrapper from "../shared/FullScreenWrapper";
import RecentUpgradesTicker from "../shared/RecentUpgradesTicker";

const MemorialDayPricing = (): JSX.Element => {
  const { status } = useSession();
  const router = useRouter();

  const { setActivePaddleScreen, origin } = usePaddleContext();
  const { startCheckout } = usePaddleCheckout();
  const { startStripeCheckout } = useStripeCheckout();
  const { checkoutProvider, ready: checkoutProviderReady } =
    useCheckoutProvider();
  const { sendToastMessage } = useToastContext();

  const [interval, setInterval] = useState<SubscriptionIntervals>(
    SubscriptionIntervals.year
  );
  const hasResumedPendingCheckout = useRef(false);

  // Resume the plan selected before signup/login exactly once.
  useEffect(() => {
    if (status !== "authenticated" || hasResumedPendingCheckout.current) {
      return;
    }

    if (!getPendingCheckout()) {
      return;
    }

    if (!checkoutProviderReady) {
      return;
    }

    if (checkoutProvider === "stripe") {
      const pending = takePendingCheckout();
      if (!pending) {
        return;
      }

      hasResumedPendingCheckout.current = true;
      const promoCode =
        pending.discountCode || getCheckoutPromoCode() || undefined;

      void (async () => {
        try {
          await startStripeCheckout({
            priceId: pending.priceId,
            origin: origin ?? "pending-checkout",
            promoCode,
          });
        } catch (error) {
          console.error("Unable to resume Stripe checkout", error);
          const message =
            error &&
            typeof error === "object" &&
            "message" in error &&
            typeof (error as { message: unknown }).message === "string"
              ? (error as { message: string }).message
              : "Unable to start checkout. Please try again.";
          sendToastMessage(message, { severity: "error" });
        }
      })();
      return;
    }

    const resumeCheckout = () => {
      if (!window.Paddle) {
        return false;
      }

      const pending = takePendingCheckout();
      if (!pending) {
        return true;
      }

      hasResumedPendingCheckout.current = true;
      startCheckout(
        pending.priceId,
        pending.discountCode ? { code: pending.discountCode } : undefined
      );
      return true;
    };

    if (resumeCheckout()) {
      return;
    }

    // Authentication can resolve before PaddleLoader finishes. Keep the plan
    // pending until Paddle is ready instead of silently losing the checkout.
    const poll = window.setInterval(() => {
      if (resumeCheckout()) {
        window.clearInterval(poll);
      }
    }, 100);
    const timeout = window.setTimeout(() => {
      window.clearInterval(poll);
    }, 15_000);

    return () => {
      window.clearInterval(poll);
      window.clearTimeout(timeout);
    };
  }, [
    checkoutProvider,
    checkoutProviderReady,
    origin,
    sendToastMessage,
    startCheckout,
    startStripeCheckout,
    status,
  ]);

  const intervalOptions = [
    {
      label: "Monthly",
      value: SubscriptionIntervals.month,
      "aria-label": "Monthly",
    },
    {
      label: "Yearly",
      value: SubscriptionIntervals.year,
      "aria-label": "Yearly",
    },
  ];

  const availableSubscriptionPlans = useMemo(() => {
    return Object.entries(SubscriptionPlanInfo).filter(([key]) => {
      if (key === SubscriptionPlans.label) {
        return false;
      }

      return true;
    });
  }, []);

  return (
    <FullScreenWrapper
      style="gradient"
      open
      onClose={() => {
        setActivePaddleScreen(null);
        const destination = getPricingCloseDestination(
          router.pathname,
          router.asPath
        );
        void router.replace(destination, undefined, {
          shallow: true,
        });
      }}
    >
      <div className="mx-auto w-full max-w-[1380px] px-4 sm:px-5 pt-4 sm:pt-6 pb-12">
        <div className="flex flex-col gap-5 sm:gap-6">
          <Link
            href="/"
            className="flex items-center gap-2 self-start mt-2 lg:mt-0"
          >
            <Icon name="logo" className="w-6 aspect-[1.1]" />
            <Icon name="logo-text" />
          </Link>

          <div className="flex flex-col lg:flex-row items-center gap-4 mt-2">
            <div className="flex flex-1 cursor-default">
              <Card>
                <div className="px-1 flex items-center gap-2">
                  <Icon name="percent" className="flex-shrink-0" />
                  <SaleOfferHeading extensionClassName="text-12" />
                </div>
              </Card>
            </div>

            <div className="flex-1 flex items-center justify-center">
              <Switcher
                labelClassName="!px-12 !font-light"
                className="!w-fit"
                value={interval}
                setValue={setInterval}
                options={intervalOptions}
              />
            </div>

            <div className="flex-1 lg:flex hidden justify-end">
              <Button
                variant="text"
                className="text-18"
                onClick={() => {
                  document
                    .querySelector("#features-breakdown")
                    ?.scrollIntoView({
                      behavior: "smooth",
                    });
                }}
              >
                View Features
              </Button>
            </div>
          </div>

          <RecentUpgradesTicker />

          <div
            className={clsx("grid grid-cols-1 gap-4 lg:gap-6 lg:mt-4", {
              "lg:grid-cols-2": availableSubscriptionPlans.length === 4,
              "lg:grid-cols-3": availableSubscriptionPlans.length === 3,
            })}
          >
            {availableSubscriptionPlans.map(([key, details]) => {
              return (
                <MemorialDayPlanCard
                  interval={interval}
                  details={details}
                  key={key}
                />
              );
            })}
          </div>

          <div className="mt-6" id="features-breakdown">
            <FeaturesBreakdown />
          </div>

          <div className="mt-6">
            <FAQ />
          </div>
        </div>
      </div>
    </FullScreenWrapper>
  );
};

export default MemorialDayPricing;
